import { useState } from "react";
import Icon from "@mdi/react";
import { mdiClose, mdiPlus } from "@mdi/js";
import { Button, Input, TextField } from "@heroui/react";
import {
  applyPhoneMask,
  phoneMaskToRaw,
} from "@/pages/ContractorDetailPage/constants";

interface StringListEditorProps {
  items: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder: string;
  addLabel?: string;
  inputMode?: "email" | "phone";
  listError?: string;
  disabled?: boolean;
}

const StringListEditor: React.FC<StringListEditorProps> = ({
  items,
  onAdd,
  onRemove,
  placeholder,
  addLabel = "Добавить",
  inputMode = "email",
  listError,
  disabled = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  const visibleItems = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.trim() !== "");

  const resetDraft = () => {
    setDraft("");
    setDraftError(null);
  };

  const closeAdding = () => {
    setIsAdding(false);
    resetDraft();
  };

  const handleStartAdding = () => {
    if (disabled) return;
    setIsAdding(true);
    resetDraft();
  };

  const handleConfirmAdd = () => {
    if (disabled) return;

    const value =
      inputMode === "phone" ? phoneMaskToRaw(draft) : draft.trim().toLowerCase();

    if (!value) {
      setDraftError(
        inputMode === "phone" ? "Введите номер телефона" : "Введите email",
      );
      return;
    }

    if (inputMode === "phone") {
      if (!/^\+7\d{10}$/.test(value)) {
        setDraftError("Телефон должен быть в формате +7XXXXXXXXXX");
        return;
      }
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setDraftError("Некорректный email");
      return;
    }

    if (items.includes(value)) {
      setDraftError(
        inputMode === "phone"
          ? "Этот номер уже добавлен"
          : "Этот email уже добавлен",
      );
      return;
    }

    onAdd(value);
    closeAdding();
  };

  const handleInputChange = (raw: string) => {
    setDraftError(null);
    setDraft(inputMode === "phone" ? applyPhoneMask(raw) : raw);
  };

  const displayValue = (value: string) =>
    inputMode === "phone" ? applyPhoneMask(value) : value;

  return (
    <div className="fbs-string-list">
      {visibleItems.length > 0 && (
        <ul className="fbs-string-list__items">
          {visibleItems.map(({ item, index }) => (
            <li key={`${item}-${index}`} className="fbs-string-list__item">
              <span className="fbs-string-list__value">{displayValue(item)}</span>
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label="Удалить"
                isDisabled={disabled}
                onPress={() => onRemove(index)}
              >
                <Icon path={mdiClose} size={0.7} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {isAdding ? (
        <div className="fbs-string-list__draft">
          <TextField fullWidth isInvalid={!!draftError}>
            <Input
              autoFocus
              value={draft}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={placeholder}
              type={inputMode === "email" ? "email" : "tel"}
              inputMode={inputMode === "phone" ? "tel" : "email"}
              disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleConfirmAdd();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  closeAdding();
                }
              }}
            />
          </TextField>
          <div className="fbs-string-list__draft-actions">
            <Button
              variant="primary"
              size="sm"
              onPress={handleConfirmAdd}
              isDisabled={disabled}
            >
              Добавить
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onPress={closeAdding}
              isDisabled={disabled}
            >
              Отмена
            </Button>
          </div>
          {draftError && (
            <span className="fbs-form-field__error">{draftError}</span>
          )}
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="fbs-string-list__add-btn"
          onPress={handleStartAdding}
          isDisabled={disabled}
        >
          <Icon path={mdiPlus} size={0.75} />
          {addLabel}
        </Button>
      )}

      {!isAdding && listError && (
        <span className="fbs-form-field__error">{listError}</span>
      )}
    </div>
  );
};

StringListEditor.displayName = "StringListEditor";
export default StringListEditor;
